import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useAPI } from '../../Contexts/APIContext'
import { ButtonOutline } from '../../Components/GeneralComponents/ButtonOutline/ButtonOutline'
import { ModalWindow } from '../../Components/GeneralComponents/ModalWindow/ModalWindow'
import Loader from '../../Components/GeneralComponents/Loader'
import Prompt from '../../Components/DashboardComponents/Prompt/Prompt'
import errorToast from '../../Components/Toasts/ErrorToast'
import {
  MAX_WEBP_BYTES,
  WEBP_REQUIREMENTS_COPY,
  toTitleCase,
  warnIfNotPortrait916,
} from './webpRequirements'
import classes from './index.module.css'

// A published feels whose rendered video plays under the overlay in the comped
// preview. A feels f_id is NOT its render filename, so we resolve it via
// getFeelData (GET /feels/mm/:f_id) — the same call the Feel page uses; the video
// URL lives at preview.video.video.url.
const DEFAULT_DEMO_FEEL_FID = 'ntAGGd3qFe7X1tJXhKKRgTMsX2jADM0n'

function statusLabel(filter) {
  if (!filter) return ''
  if (filter.is_published) return 'published'
  return 'draft'
}

const csvToArray = value =>
  value
    .split(',')
    .map(v => v.trim())
    .filter(Boolean)

export function FilterDetail({ filterFID, onClose, onChanged }) {
  const {
    getOverlayFilter,
    updateOverlayFilter,
    deleteOverlayFilter,
    publishOverlayFilter,
    getUrlNewOverlayFilterMedia,
    uploadOverlayFilterMedia,
    finalizeOverlayFilterMedia,
    getFeelData,
  } = useAPI()

  const [isSaving, setIsSaving] = useState(false)
  const [isUploading, setIsUploading] = useState(false)
  const [isFinalizing, setIsFinalizing] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [confirmUnpublish, setConfirmUnpublish] = useState(false)

  // editable fields
  const [name, setName] = useState('')
  const [tags, setTags] = useState('')
  const [mood, setMood] = useState('')
  const [loop, setLoop] = useState(true)

  // comped-preview state: the demo feels whose rendered video plays under the
  // overlay. Its f_id is resolved to a video URL via getFeelData.
  const [demoFeelFid, setDemoFeelFid] = useState(DEFAULT_DEMO_FEEL_FID)
  const [demoVideoUrl, setDemoVideoUrl] = useState(null)
  const [isResolvingDemo, setIsResolvingDemo] = useState(false)
  const [demoError, setDemoError] = useState('')
  // Monotonic request id: only the latest resolveDemoFeel call may commit state,
  // so out-of-order responses can't overwrite a newer resolution.
  const demoRequestRef = useRef(0)

  const queryClient = useQueryClient()

  const {
    data: filter,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ['overlayFilter', filterFID],
    queryFn: () => getOverlayFilter(filterFID),
    staleTime: 0,
  })

  // After any successful mutation, refresh BOTH the list keys and THIS detail
  // query — the app-wide staleTime:Infinity means the cached detail object would
  // otherwise stay stale and a follow-up PATCH could revert a just-changed asset.
  const invalidateAll = async () => {
    await queryClient.invalidateQueries({ queryKey: ['overlayFilters'] })
    await queryClient.invalidateQueries({
      queryKey: ['overlayFilter', filterFID],
    })
    await onChanged()
  }

  // Hydrate the form once the filter loads / changes identity.
  useEffect(() => {
    if (!filter) return
    setName(filter.name || '')
    setTags((filter.metadata?.tags || []).join(', '))
    setMood((filter.metadata?.mood || []).join(', '))
    const firstSource = filter.render_config?.sources?.[0]
    setLoop(firstSource?.loop ?? true)
  }, [filter])

  const source = filter?.render_config?.sources?.[0]
  const hasSource = !!source
  const overlayUrl = source?.url
  const isPublished = !!filter?.is_published
  const isDraft = !isPublished

  const handleSave = async () => {
    // Force Title Case at save time too, in case the user edited then hit SAVE
    // without the input ever blurring.
    const finalName = toTitleCase(name.trim())
    if (!finalName) {
      errorToast('Name is required')
      return
    }
    setIsSaving(true)
    try {
      // Read the freshest filter (not the possibly-stale query closure) so a
      // SAVE right after a REPLACE MEDIA builds render_config from the NEW
      // sources instead of reverting them.
      const current = await getOverlayFilter(filterFID)
      const render_config = current.render_config
        ? {
            // blend_mode/fit pass through untouched from the server; the only
            // admin-editable source field is loop. width/height/duration_ms/
            // content_hash are server-derived.
            ...current.render_config,
            sources: (current.render_config.sources || []).map(s => ({
              ...s,
              loop: loop,
            })),
          }
        : undefined
      await updateOverlayFilter(filterFID, {
        name: finalName,
        metadata: { tags: csvToArray(tags), mood: csvToArray(mood) },
        ...(render_config ? { render_config } : {}),
      })
      await invalidateAll()
    } catch (e) {
      errorToast(`Error: ${e.status ?? ''} ${e.message ?? ''}`)
    } finally {
      setIsSaving(false)
    }
  }

  const handleUpload = async event => {
    const file = event.target.files?.[0]
    event.target.value = '' // allow re-selecting the same file
    if (!file) return
    if (file.type !== 'image/webp') {
      errorToast('Only animated WebP (.webp) files are allowed')
      return
    }
    if (file.size > MAX_WEBP_BYTES) {
      errorToast('File must be 10MB or smaller')
      return
    }
    setIsUploading(true)
    try {
      const { upload_id, upload_url } = await getUrlNewOverlayFilterMedia()
      await uploadOverlayFilterMedia(upload_url, file)
      setIsUploading(false)
      setIsFinalizing(true)
      // Server probes the WebP and derives dims/duration/frame_count; it may
      // reject (e.g. not an animated WebP).
      const media = await finalizeOverlayFilterMedia(upload_id)
      warnIfNotPortrait916(media.width, media.height)
      await updateOverlayFilter(filterFID, {
        render_config: {
          // blend_mode/fit preserved from the existing config, if any.
          ...(filter.render_config || {}),
          sources: [
            {
              format: 'animated_webp',
              media_f_id: media.media_f_id,
              content_hash: media.content_hash,
              width: media.width,
              height: media.height,
              fps: 30,
              duration_ms: media.duration_ms,
              loop: true,
            },
          ],
        },
      })
      await invalidateAll()
    } catch (e) {
      errorToast(`Upload failed: ${e.status ?? ''} ${e.message ?? ''}`)
    } finally {
      setIsUploading(false)
      setIsFinalizing(false)
    }
  }

  const handlePublishToggle = async isPublishing => {
    setIsSaving(true)
    try {
      await publishOverlayFilter(filterFID, isPublishing)
      await invalidateAll()
    } catch (e) {
      errorToast(`Error: ${e.status ?? ''} ${e.message ?? ''}`)
    } finally {
      setIsSaving(false)
      setConfirmUnpublish(false)
    }
  }

  const handleDelete = async () => {
    setConfirmDelete(false)
    setIsSaving(true)
    try {
      await deleteOverlayFilter(filterFID)
      await invalidateAll()
      onClose()
    } catch (e) {
      errorToast(`Error: ${e.status ?? ''} ${e.message ?? ''}`)
      setIsSaving(false)
    }
  }

  // Resolve a feels f_id to its rendered video URL (preview.video.video.url),
  // the same shape the Feel page reads from getFeelData.
  const resolveDemoFeel = async fid => {
    const trimmed = (fid || '').trim()
    const requestId = ++demoRequestRef.current
    const isStale = () => requestId !== demoRequestRef.current
    if (!trimmed) {
      setDemoVideoUrl(null)
      setDemoError('')
      return
    }
    setIsResolvingDemo(true)
    setDemoError('')
    try {
      const feel = await getFeelData(trimmed)
      if (isStale()) return
      const url = feel?.preview?.video?.video?.url
      if (url) {
        setDemoVideoUrl(url)
      } else {
        setDemoVideoUrl(null)
        setDemoError('No rendered video for that feels f_id')
      }
    } catch (e) {
      if (isStale()) return
      setDemoVideoUrl(null)
      setDemoError(
        e.status === 404
          ? 'Feels f_id not found'
          : `Could not load feels (${e.status ?? ''} ${e.message ?? ''})`.trim()
      )
    } finally {
      if (!isStale()) setIsResolvingDemo(false)
    }
  }

  // Resolve the default demo feels once on open.
  useEffect(() => {
    resolveDemoFeel(DEFAULT_DEMO_FEEL_FID)
  }, [])

  const busy = isSaving || isUploading || isFinalizing

  // Escape closes the panel, but not while a save/upload is in flight (matches the
  // onClose guard). If a confirm Prompt is open, Escape dismisses only that Prompt,
  // leaving the panel open. ModalWindow has no key handling of its own. No dep array
  // so the handler always reads the latest state; re-subscribing each render is cheap.
  useEffect(() => {
    const onKeyDown = e => {
      if (e.key !== 'Escape') return
      if (confirmDelete) {
        setConfirmDelete(false)
        return
      }
      if (confirmUnpublish) {
        setConfirmUnpublish(false)
        return
      }
      if (!busy) onClose()
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  })

  return (
    <ModalWindow open onClose={() => !busy && onClose()}>
      {confirmDelete && (
        <Prompt
          title="Confirm Deletion of Filter"
          subtitle="Are you sure you want to delete this overlay filter? It will be pulled from the app. Existing feels keep their snapshot, but this cannot be undone."
          primaryAction="CANCEL"
          onPrimaryClick={() => setConfirmDelete(false)}
          secondaryAction="DELETE"
          onSecondaryClick={handleDelete}
          isSecondaryActionDestructive={true}
        />
      )}
      {confirmUnpublish && (
        <Prompt
          title="Unpublish Filter"
          subtitle="Unpublishing pulls this filter from the app immediately. Existing feels are unaffected. Continue?"
          primaryAction="CANCEL"
          onPrimaryClick={() => setConfirmUnpublish(false)}
          secondaryAction="UNPUBLISH"
          onSecondaryClick={() => handlePublishToggle(false)}
          isSecondaryActionDestructive={true}
        />
      )}

      <div className={classes.detail}>
        {isLoading && (
          <div className={classes.loader}>
            <Loader isLoading={isLoading} />
          </div>
        )}
        {isError && (
          <p style={{ color: 'white' }}>Failed to load filter.</p>
        )}

        {filter && (
          <div className={classes.detailBody}>
            {/* Comped preview */}
            <div className={classes.previewColumn}>
              <div className={classes.previewBox}>
                {demoVideoUrl && (
                  <video
                    key={demoVideoUrl}
                    className={classes.previewVideo}
                    src={demoVideoUrl}
                    autoPlay
                    loop
                    muted
                    playsInline
                  />
                )}
                {overlayUrl && (
                  <img
                    className={classes.previewOverlay}
                    src={overlayUrl}
                    alt="overlay preview"
                  />
                )}
                {!overlayUrl && (
                  <div className={classes.previewEmpty}>
                    Upload media to preview
                  </div>
                )}
              </div>
              <div className={classes.previewControls}>
                <label className={classes.fieldLabel}>Demo feel f_id</label>
                <div className={classes.demoFidRow}>
                  <input
                    className={classes.textInput}
                    value={demoFeelFid}
                    placeholder="feels f_id"
                    onChange={e => setDemoFeelFid(e.target.value)}
                    onBlur={e => resolveDemoFeel(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Enter') resolveDemoFeel(demoFeelFid)
                    }}
                  />
                  <ButtonOutline
                    text={isResolvingDemo ? 'LOADING…' : 'LOAD'}
                    color="pinkBlack"
                    size="small"
                    disable={isResolvingDemo}
                    onClick={() => resolveDemoFeel(demoFeelFid)}
                  />
                </div>
                {demoError && (
                  <span className={classes.demoError}>{demoError}</span>
                )}
              </div>
            </div>

            {/* Edit form */}
            <div className={classes.formColumn}>
              <div className={classes.detailHeader}>
                <span
                  className={`${classes.statusChip} ${
                    classes[`status_${statusLabel(filter)}`]
                  }`}
                >
                  {statusLabel(filter)}
                </span>
                <span className={classes.versionText}>
                  v{filter.version ?? 1}
                </span>
              </div>

              <label className={classes.fieldLabel}>Token</label>
              <input
                className={classes.textInput}
                value={filter.token}
                disabled
              />
              {isPublished && (
                <span className={classes.fieldHint}>
                  Token is frozen after publish.
                </span>
              )}

              <label className={classes.fieldLabel}>Name</label>
              <input
                className={classes.textInput}
                value={name}
                onChange={e => setName(e.target.value)}
                onBlur={e => setName(toTitleCase(e.target.value))}
              />

              <label className={classes.fieldLabel}>Tags</label>
              <input
                className={classes.textInput}
                placeholder="comma-separated"
                value={tags}
                onChange={e => setTags(e.target.value)}
              />

              <label className={classes.fieldLabel}>Mood</label>
              <input
                className={classes.textInput}
                placeholder="comma-separated"
                value={mood}
                onChange={e => setMood(e.target.value)}
              />

              <div className={classes.sectionTitle}>Render config</div>
              {hasSource ? (
                <div className={classes.sourceBox}>
                  <div className={classes.sourceRow}>
                    <span>Format</span>
                    <span>{source.format}</span>
                  </div>
                  <div className={classes.sourceRow}>
                    <span>Dimensions</span>
                    <span>
                      {source.width} × {source.height}
                    </span>
                  </div>
                  <div className={classes.sourceRow}>
                    <span>Duration</span>
                    <span>{source.duration_ms} ms</span>
                  </div>
                  <div className={classes.sourceRow}>
                    <span>Content hash</span>
                    <span className={classes.hashText}>
                      {source.content_hash}
                    </span>
                  </div>
                  <label className={classes.checkboxRow}>
                    <input
                      type="checkbox"
                      checked={loop}
                      onChange={e => setLoop(e.target.checked)}
                    />
                    Loop
                  </label>
                  <span className={classes.fieldHint}>
                    Dimensions, duration and hash are derived by the server and
                    are read-only.
                  </span>
                </div>
              ) : (
                <div className={classes.noSource}>
                  No media uploaded yet. Upload an animated WebP to enable
                  preview and publishing.
                </div>
              )}

              <p className={classes.requirementsBlock}>
                {WEBP_REQUIREMENTS_COPY}
              </p>
              <label className={classes.uploadButton}>
                {isUploading
                  ? 'UPLOADING…'
                  : isFinalizing
                  ? 'PROCESSING…'
                  : hasSource
                  ? 'REPLACE MEDIA (.webp)'
                  : 'UPLOAD MEDIA (.webp)'}
                <input
                  type="file"
                  accept="image/webp,.webp"
                  onChange={handleUpload}
                  disabled={busy}
                  style={{ display: 'none' }}
                />
              </label>
              {(isUploading || isFinalizing) && (
                <div className={classes.inlineLoader}>
                  <Loader isLoading={true} />
                </div>
              )}

              <div className={classes.detailActions}>
                <ButtonOutline
                  text={isSaving ? 'SAVING…' : 'SAVE'}
                  color="pink"
                  disable={busy}
                  onClick={handleSave}
                />
                {!isPublished && (
                  <ButtonOutline
                    text="PUBLISH"
                    color="green"
                    disable={busy || !hasSource}
                    tooltip={
                      !hasSource ? 'Upload media before publishing' : undefined
                    }
                    onClick={() => handlePublishToggle(true)}
                  />
                )}
                {isPublished && (
                  <ButtonOutline
                    text="UNPUBLISH"
                    color="pinkBlack"
                    disable={busy}
                    onClick={() => setConfirmUnpublish(true)}
                  />
                )}
                <ButtonOutline
                  text="DELETE"
                  color="red"
                  disable={busy}
                  onClick={() => setConfirmDelete(true)}
                />
              </div>
              {isDraft && (
                <span className={classes.fieldHint}>
                  This filter is a draft — it is not visible in the app until
                  published.
                </span>
              )}
            </div>
          </div>
        )}
      </div>
    </ModalWindow>
  )
}

FilterDetail.propTypes = {
  filterFID: PropTypes.string,
  onClose: PropTypes.func,
  onChanged: PropTypes.func,
}
